/**
 * WB4 Depth — Phase D+: Role composition registry (pilot).
 * Configuration (`workbench-role-cockpit-config`) = what to show;
 * composition = how existing surfaces are arranged in the unified shell.
 */

import type {
  RoleNavigatorConfig,
  TenantRoleConfigPartial,
  WorkbenchRole,
} from './workbench-role-cockpit-config';
import {
  resolveRoleNavigatorDomains,
  type WorkbenchNavigatorDomainEntry,
} from './workbench-role-navigator-queue';

export type RoleCompositionLayoutId = 'balanced' | 'queue_first_split' | 'navigator_list_emphasis';

export type RoleCompositionPageMode = 'shell' | 'full_page';

/** Route-level landing page — pilot: sales · warehouse · finance · admin · owner. */
export type RoleCompositionPageId =
  | 'sales_queue_dashboard'
  | 'warehouse_ops_board'
  | 'finance_exceptions_receivables_queue'
  | 'collection_manager_exception_queue'
  | 'collection_dashboard'
  | 'collection_enforcement'
  | 'collection_operator_projection'
  | 'admin_permission_audit_board'
  | 'owner_executive_cockpit';

export type RoleCompositionEmphasisZone = 'navigator' | 'primary' | 'inspector' | 'auxiliary';

export type RoleCompositionNavigatorPanelMode = 'domain_first' | 'queue_first';

export type RoleCompositionNavigatorTabKey = 'queue' | 'exceptions' | 'related' | 'approvals';

export interface RoleCompositionNavigatorPanel {
  /** Tab order in domain navigator panel — role-specific cockpit differentiation. */
  tabOrder: readonly RoleCompositionNavigatorTabKey[];
  /** When true, shell renders approvals inbox tab (finance/admin pilot). */
  showApprovalsTab?: boolean;
}

export interface RoleCompositionZoneGrid {
  /** CSS grid-template-columns for shell body row (navigator · primary · inspector). */
  columns: string;
  navigatorMinWidth?: number;
  inspectorMaxWidth?: number;
}

export interface RoleCompositionSpec {
  role: WorkbenchRole;
  layoutId: RoleCompositionLayoutId;
  zoneGrid: RoleCompositionZoneGrid;
  emphasisZones: readonly RoleCompositionEmphasisZone[];
  navigatorPanelMode: RoleCompositionNavigatorPanelMode;
  navigatorPanel: RoleCompositionNavigatorPanel;
  /** Landing surface hint — aligns with RoleNavigatorConfig.defaultLanding.surface. */
  landingSurface: 'list' | 'dashboard' | 'queue';
  /** Zone shell (default) vs dedicated full-page landing route. */
  pageMode: RoleCompositionPageMode;
  /** Full-page pilot id — undefined when pageMode is shell. */
  landingPageId?: RoleCompositionPageId;
  /** Workspace-relative landing route — pilot only. */
  landingRoute?: string | undefined;
}

const BALANCED_ZONE_GRID: RoleCompositionZoneGrid = {
  columns: '200px minmax(0, 1fr) 260px',
  navigatorMinWidth: 200,
  inspectorMaxWidth: 260,
};

const DEFAULT_NAVIGATOR_PANEL: RoleCompositionNavigatorPanel = {
  tabOrder: ['queue', 'exceptions', 'related'],
};

const BALANCED_COMPOSITION_BASE: Omit<RoleCompositionSpec, 'role'> = {
  layoutId: 'balanced',
  zoneGrid: BALANCED_ZONE_GRID,
  emphasisZones: [],
  navigatorPanelMode: 'domain_first',
  navigatorPanel: DEFAULT_NAVIGATOR_PANEL,
  landingSurface: 'list',
  pageMode: 'shell',
};

export const ROLE_COMPOSITION_LANDING_ROUTES: Readonly<Partial<Record<WorkbenchRole, string>>> = {
  sales: '/workspace/sales',
  warehouse: '/workspace/warehouse',
  finance: '/workspace/finance',
  admin: '/workspace/admin',
  owner: '/workspace/owner',
};

/** Pilot matrix — sales vs warehouse get distinct layouts; others stay balanced. */
export const ROLE_COMPOSITION_REGISTRY: Readonly<
  Partial<Record<WorkbenchRole, RoleCompositionSpec>>
> = {
  sales: {
    role: 'sales',
    layoutId: 'queue_first_split',
    zoneGrid: {
      columns: '280px minmax(0, 1fr) 240px',
      navigatorMinWidth: 280,
      inspectorMaxWidth: 240,
    },
    emphasisZones: ['navigator'],
    navigatorPanelMode: 'queue_first',
    navigatorPanel: {
      tabOrder: ['queue', 'exceptions', 'related'],
    },
    landingSurface: 'queue',
    pageMode: 'full_page',
    landingPageId: 'sales_queue_dashboard',
    landingRoute: ROLE_COMPOSITION_LANDING_ROUTES.sales,
  },
  warehouse: {
    role: 'warehouse',
    layoutId: 'navigator_list_emphasis',
    zoneGrid: {
      columns: '240px minmax(0, 1.2fr) 200px',
      navigatorMinWidth: 240,
      inspectorMaxWidth: 200,
    },
    emphasisZones: ['navigator', 'primary'],
    navigatorPanelMode: 'domain_first',
    navigatorPanel: {
      tabOrder: ['queue', 'exceptions'],
    },
    landingSurface: 'list',
    pageMode: 'full_page',
    landingPageId: 'warehouse_ops_board',
    landingRoute: ROLE_COMPOSITION_LANDING_ROUTES.warehouse,
  },
  finance: {
    role: 'finance',
    layoutId: 'queue_first_split',
    zoneGrid: {
      columns: '280px minmax(0, 1fr) 240px',
      navigatorMinWidth: 280,
      inspectorMaxWidth: 240,
    },
    emphasisZones: ['navigator'],
    navigatorPanelMode: 'queue_first',
    navigatorPanel: {
      tabOrder: ['exceptions', 'approvals', 'queue', 'related'],
      showApprovalsTab: true,
    },
    landingSurface: 'queue',
    pageMode: 'full_page',
    landingPageId: 'finance_exceptions_receivables_queue',
    landingRoute: ROLE_COMPOSITION_LANDING_ROUTES.finance,
  },
  admin: {
    role: 'admin',
    layoutId: 'navigator_list_emphasis',
    zoneGrid: {
      columns: '240px minmax(0, 1.2fr) 200px',
      navigatorMinWidth: 240,
      inspectorMaxWidth: 200,
    },
    emphasisZones: ['navigator', 'primary'],
    navigatorPanelMode: 'domain_first',
    navigatorPanel: {
      tabOrder: ['exceptions', 'approvals', 'queue', 'related'],
      showApprovalsTab: true,
    },
    landingSurface: 'dashboard',
    pageMode: 'full_page',
    landingPageId: 'admin_permission_audit_board',
    landingRoute: ROLE_COMPOSITION_LANDING_ROUTES.admin,
  },
  owner: {
    role: 'owner',
    layoutId: 'queue_first_split',
    zoneGrid: {
      columns: '280px minmax(0, 1fr) 240px',
      navigatorMinWidth: 280,
      inspectorMaxWidth: 240,
    },
    emphasisZones: ['navigator'],
    navigatorPanelMode: 'queue_first',
    navigatorPanel: {
      tabOrder: ['queue', 'exceptions', 'related'],
    },
    landingSurface: 'dashboard',
    pageMode: 'full_page',
    landingPageId: 'owner_executive_cockpit',
    landingRoute: ROLE_COMPOSITION_LANDING_ROUTES.owner,
  },
  operations: {
    role: 'operations',
    layoutId: 'navigator_list_emphasis',
    zoneGrid: {
      columns: '240px minmax(0, 1.2fr) 200px',
      navigatorMinWidth: 240,
      inspectorMaxWidth: 200,
    },
    emphasisZones: ['navigator', 'primary'],
    navigatorPanelMode: 'domain_first',
    navigatorPanel: DEFAULT_NAVIGATOR_PANEL,
    landingSurface: 'list',
    pageMode: 'shell',
  },
};

export function resolveRoleComposition(role: WorkbenchRole): RoleCompositionSpec {
  return (
    ROLE_COMPOSITION_REGISTRY[role] ?? {
      role,
      ...BALANCED_COMPOSITION_BASE,
    }
  );
}

export interface RoleCompositionContextInput {
  role: WorkbenchRole;
  /** Tenant navigator/preset partial — composition layout remains registry SSOT (DL-D). */
  tenantOverride?: TenantRoleConfigPartial | null;
}

/**
 * Role cockpit composition for runtime context.
 *
 * @defer tenant-editable composition overrides — layoutId · zoneGrid · landingRoute remain
 *   registry SSOT until server merge is scoped. Tenant preset merges navigator/preset via
 *   `mergeTenantRoleConfig` (workspace layer); zone visibility flows through `preset.zones`.
 *   Auxiliary zone drag-reorder is personal-layer only (`auxiliaryTabOrder` defer for tenant).
 *
 * Gate: `pnpm run check:role-composition-routes`
 */
export function resolveRoleCompositionForContext(
  input: RoleCompositionContextInput
): RoleCompositionSpec {
  void input.tenantOverride;
  return resolveRoleComposition(input.role);
}

export function isRoleCompositionEmphasisZone(
  composition: RoleCompositionSpec,
  zone: RoleCompositionEmphasisZone
): boolean {
  return composition.emphasisZones.includes(zone);
}

export function shouldUseRoleCompositionFullPage(composition: RoleCompositionSpec): boolean {
  return composition.pageMode === 'full_page' && composition.landingPageId !== undefined;
}

export function resolveRoleCompositionLandingRoute(role: WorkbenchRole): string | undefined {
  return resolveRoleComposition(role).landingRoute;
}

export function resolveRoleCompositionPageId(
  role: WorkbenchRole
): RoleCompositionPageId | undefined {
  return resolveRoleComposition(role).landingPageId;
}

/** Navigator domain lens — composition panel mode + cockpit config queues/landing. */
export function resolveRoleCompositionNavigatorDomains(
  role: WorkbenchRole,
  navigatorConfig: Pick<RoleNavigatorConfig, 'defaultLanding' | 'queues'>
): WorkbenchNavigatorDomainEntry[] {
  const composition = resolveRoleComposition(role);
  return resolveRoleNavigatorDomains(role, navigatorConfig, composition);
}

export function resolveRoleCompositionNavigatorTabOrder(
  composition: RoleCompositionSpec
): readonly RoleCompositionNavigatorTabKey[] {
  return composition.navigatorPanel.tabOrder;
}
